import React, { useCallback, useEffect, useState } from "react";
import {
  adminListSupportRequests,
  adminRespondSupportRequest,
  adminSetSupportRequestStatus,
} from "../../api";

const CATEGORY_LABELS = { issue: "Issue", feedback: "Feedback", feature: "Feature request" };
const FILTERS = [
  ["", "All"],
  ["open", "Open"],
  ["resolved", "Resolved"],
];

/**
 * SupportAdminPanel — staff surface for the help-widget queue. Lists all
 * support requests with a status filter; expanding a row shows the full
 * message plus a response box. Responding emails the user (best-effort — a
 * warning is shown if delivery fails; the response is always visible to the
 * user in-app) and resolves by default.
 *
 * Props: onClose.
 */
export default function SupportAdminPanel({ onClose }) {
  const [filter, setFilter] = useState("open");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminListSupportRequests(filter)
      .then(setRequests)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(load, [load]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const patchRow = (updated) =>
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));

  const respond = async (req, resolve) => {
    if (!draft.trim()) {
      setNotice({ kind: "error", text: "Write a response first." });
      return;
    }
    setBusyId(req.id);
    setNotice(null);
    try {
      const updated = await adminRespondSupportRequest(req.id, {
        response: draft.trim(),
        resolve,
      });
      patchRow(updated);
      setDraft("");
      setNotice(
        updated.email_sent
          ? { kind: "ok", text: `Response sent to ${updated.user_email}.` }
          : {
              kind: "warn",
              text: "Response saved, but the email failed to send — the user will still see it in-app.",
            }
      );
    } catch (e) {
      setNotice({ kind: "error", text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (req, status) => {
    setBusyId(req.id);
    setNotice(null);
    try {
      const updated = await adminSetSupportRequestStatus(req.id, status);
      patchRow(updated);
    } catch (e) {
      setNotice({ kind: "error", text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const toggleRow = (req) => {
    setNotice(null);
    setDraft("");
    setOpenId((cur) => (cur === req.id ? null : req.id));
  };

  const noticeStyle = {
    ok: "text-emerald-700 bg-emerald-50 border-emerald-200",
    warn: "text-amber-800 bg-amber-50 border-amber-200",
    error: "text-rose-700 bg-rose-50 border-rose-200",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
      onClick={onClose}
    >
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-3xl h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-6 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--panel-line)" }}
        >
          <div>
            <h2 className="font-cormorant font-semibold text-lg" style={{ color: "var(--gold-bright)" }}>
              Support Requests
            </h2>
            <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>
              Issues, feedback, and feature requests raised from the in-app help button
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 px-6 pt-3 shrink-0">
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              onClick={() => {
                setFilter(value);
                setOpenId(null);
              }}
              className={
                "px-3 py-1.5 rounded-lg text-sm font-semibold transition " +
                (filter === value ? "bg-white/10" : "opacity-60 hover:opacity-100")
              }
              style={{ color: "var(--parchment)" }}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="px-6 py-4 overflow-y-auto scroll-elegant flex-1">
          {error && (
            <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3">
              {error}
            </p>
          )}
          {loading && <p className="text-sm py-4 text-center" style={{ color: "var(--parchment-dim)" }}>Loading…</p>}
          {!loading && requests.length === 0 && (
            <p className="text-sm py-4 text-center" style={{ color: "var(--parchment-dim)" }}>
              No {filter || "support"} requests.
            </p>
          )}

          <div className="flex flex-col gap-2">
            {requests.map((req) => {
              const expanded = openId === req.id;
              const resolved = req.status === "resolved";
              return (
                <div
                  key={req.id}
                  className="rounded-xl border"
                  style={{ borderColor: "var(--panel-line)" }}
                >
                  <button
                    onClick={() => toggleRow(req)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3"
                  >
                    <span
                      className={
                        "text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 " +
                        (resolved
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200")
                      }
                    >
                      {resolved ? "Resolved" : "Open"}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold truncate" style={{ color: "var(--parchment)" }}>
                        {req.subject}
                      </span>
                      <span className="block text-xs truncate" style={{ color: "var(--parchment-dim)" }}>
                        {CATEGORY_LABELS[req.category] || req.category} · {req.user_email} ·{" "}
                        {new Date(req.created_at).toLocaleDateString()}
                      </span>
                    </span>
                    {req.response && !resolved && (
                      <span className="text-[11px] shrink-0" style={{ color: "var(--gold-bright)" }}>
                        responded
                      </span>
                    )}
                  </button>

                  {expanded && (
                    <div className="px-4 pb-4 border-t" style={{ borderColor: "var(--panel-line)" }}>
                      <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: "var(--parchment)" }}>
                        {req.message}
                      </p>

                      {req.response && (
                        <div className="mt-3 ms-3 border-s-2 ps-3" style={{ borderColor: "var(--gold-bright)" }}>
                          <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--parchment-dim)" }}>
                            Response · {req.responded_by_email || "staff"}
                            {req.responded_at ? ` · ${new Date(req.responded_at).toLocaleDateString()}` : ""}
                          </p>
                          <p className="text-sm mt-0.5 whitespace-pre-wrap" style={{ color: "var(--parchment)" }}>
                            {req.response}
                          </p>
                        </div>
                      )}

                      {notice && (
                        <p className={`text-sm border rounded-lg px-3 py-2 mt-3 ${noticeStyle[notice.kind]}`}>
                          {notice.text}
                        </p>
                      )}

                      <textarea
                        className="dt-field w-full rounded-xl px-3 py-2 text-sm mt-3"
                        rows={3}
                        placeholder={req.response ? "Send a follow-up response…" : "Write a response to the user…"}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        maxLength={4000}
                      />
                      <div className="flex flex-wrap gap-2 mt-2">
                        <button
                          onClick={() => respond(req, true)}
                          disabled={busyId === req.id}
                          className="px-3 py-2 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 disabled:opacity-50 transition-colors"
                        >
                          Send response &amp; resolve
                        </button>
                        <button
                          onClick={() => respond(req, false)}
                          disabled={busyId === req.id}
                          className="dt-ghost-btn px-3 py-2 rounded-xl text-sm"
                        >
                          Send, keep open
                        </button>
                        <span className="flex-1" />
                        {resolved ? (
                          <button
                            onClick={() => setStatus(req, "open")}
                            disabled={busyId === req.id}
                            className="dt-ghost-btn px-3 py-2 rounded-xl text-sm"
                          >
                            Reopen
                          </button>
                        ) : (
                          <button
                            onClick={() => setStatus(req, "resolved")}
                            disabled={busyId === req.id}
                            className="dt-ghost-btn px-3 py-2 rounded-xl text-sm"
                          >
                            Resolve without responding
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
